'use client';

import { cn } from '@ui/lib/utils';
import { createClient } from '@/lib/supabase/client';
import { Button } from '@ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ui/components/ui/card';
import { Input, PasswordInput } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState, useTransition } from 'react';
import { useTranslations } from 'next-intl';
import GoogleAuthButton from './google-auth-button';
import { toast } from '@ui/components/ui/sonner';

export function LoginForm({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  const t = useTranslations('Form');
  const tCommon = useTranslations('Common');
  const searchParams = useSearchParams();
  const emailFromUrl = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailFromUrl);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, startTransition] = useTransition();
  const router = useRouter();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const supabase = createClient();
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      if (data.user.deleted_at) {
        supabase.auth.signOut();
        throw new Error(t('logIn.userDeleted'));
      }

      startTransition(() => {
        router.push('/products');
      });
    } catch (error: unknown) {
      console.error('Login error:', error);
      setError(error instanceof Error ? error.message : tCommon('errors.generic'));
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (emailFromUrl) {
      setTimeout(() => toast.success(t('signUp.success')), 1000);
    }
  }, [emailFromUrl, t]);

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t('logIn.title')}</CardTitle>
          <CardDescription>{t('logIn.description')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <form onSubmit={handleLogin}>
            <div className="flex flex-col gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email">{t('email.label')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('email.placeholder')}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">{t('password.label')}</Label>
                  <Link href="/auth/forgot-password" className="ml-auto inline-block text-sm underline-offset-4 hover:underline">
                    {t('password.forgot')}
                  </Link>
                </div>
                <PasswordInput
                  id="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  placeholder={t('password.placeholder')}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus={!!emailFromUrl}
                  hideText={t('password.hide')}
                  showText={t('password.show')}
                />
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <Button type="submit" className="w-full" disabled={isLoading || isPending}>
                {isLoading || isPending ? tCommon('states.loading') : t('logIn.button')}
              </Button>
            </div>
          </form>
          <GoogleAuthButton />
          <div className="text-center text-sm">
            {t('logIn.dontHaveAccount')}
            <Link href="/auth/sign-up" className="underline underline-offset-4">
              {t('logIn.signUpLink')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
